import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { findService, SERVICES } from '../data/servicesData';
import { breadcrumbSchema, webPageSchema, serviceSchema } from '../lib/seo';
import { Button } from '../components/ui/button';

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const svc = findService(slug);
  if (!svc) return <Navigate to="/services" replace />;

  const path = `/services/${svc.slug}`;
  const bc = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: svc.name, path },
  ];

  const schemas = [
    webPageSchema({ path, name: svc.seoTitle, description: svc.seoDescription }),
    breadcrumbSchema(bc),
    serviceSchema({ name: svc.name, description: svc.body, path }),
  ];

  // Related services (all others)
  const related = SERVICES.filter(s => s.slug !== svc.slug);

  return (
    <div className="bg-[#EBE4DE] text-[#2E2C28] min-h-screen">
      <SEO title={svc.seoTitle} description={svc.seoDescription} path={path} schemas={schemas} />
      <Navbar />
      <main className="pt-36 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={bc} />

          <header className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <p className="sec-num text-sm tracking-[0.3em] uppercase">/ Service</p>
            </div>
            <div className="lg:col-span-8">
              <h1 className="font-serif text-[40px] sm:text-[52px] lg:text-[68px] leading-[1.02] tracking-[-0.015em] text-[#2E2C28] font-light">
                {svc.name}
              </h1>
              <p className="mt-8 text-lg lg:text-xl text-[#4a463f] font-light leading-relaxed max-w-2xl">
                {svc.body}
              </p>
            </div>
          </header>

          <section aria-label="How it fits" className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 border-t border-[#d9d2ca] pt-16">
            <div className="lg:col-span-4">
              <h2 className="font-serif text-2xl lg:text-3xl text-[#2E2C28] font-light">How this fits the Founder Growth Collective approach</h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-base lg:text-lg text-[#4a463f] font-light leading-relaxed max-w-3xl">
              <p>Founder Growth Collective partners with a select group of funded founders to architect a bespoke sales function that reflects your actual market stage, not a generic template.</p>
              <p>Funded startups struggle with sales when the founder remains the only closer. Scaling is impossible when every lead requires your direct involvement. Founder Growth Collective builds the autonomous systems that empower your business to accelerate without you.</p>
            </div>
          </section>

          <section aria-label="Related services" className="mt-24 border-t border-[#d9d2ca] pt-16">
            <h2 className="font-serif text-2xl lg:text-3xl text-[#2E2C28] font-light mb-10">Related services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map(r => (
                <Link key={r.slug} to={`/services/${r.slug}`} className="group block border border-[#d9d2ca] hover:border-[#B5876C] p-6 transition-colors">
                  <p className="sec-num text-xs tracking-[0.24em] uppercase mb-4">Service</p>
                  <h3 className="font-serif text-xl text-[#2E2C28] font-light leading-tight group-hover:text-[#B5876C] transition-colors">{r.name}</h3>
                </Link>
              ))}
            </div>
          </section>

          <div className="mt-16">
            <Button asChild className="bg-[#2E2C28] hover:bg-[#B5876C] text-[#EBE4DE] rounded-none h-14 px-8 text-[12px] tracking-[0.2em] uppercase">
              <Link to="/contact" className="inline-flex items-center gap-3">Book a Discovery Call <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
